import { PromptStackItem } from "../AI/Prompt.Interface";
import { PaymentSessionMode } from "../Payments/PaymentSession.Interfaces";
import { GenericTableColumns } from "../Table/Table.Interfaces";
import { TicketPriority, TicketStatus } from "../Tickets/Ticketing.Enums";
import { VarKey } from "../Variables/Generated/VarKey.Data";

export type NodeMap = { [nodeId: string]: NodeInterface };

export enum NodeType {
  // CRM = 'crm',
  // StoreInfo            = "storeInfo"    ,
  Action = "action",
  Analytics = "analytics",
  Array = "array",
  ArrayPicker = "arrayPicker",
  BrainResult = "brainResult",
  Buttons = "buttons",
  Carousel = "carousel",
  Command = "command",
  Continue = "continue",
  DateAndTime = "dateAndTime",
  Email = "email",
  Escalation = "escalation",
  // Experiment = 'experiment',
  // ExperimentEnd = 'experimentEnd',
  Finish = "finish",
  Hubspot = "hubspot",
  Increment = "increment",
  Input = "input",
  LeasePicker = "leasePicker",
  LLM = "llm",
  LoadData = "loadData",
  Logic = "logic",
  Media = "media",
  MergeUser = "mergeUser",
  Message = "message",
  MultiSelect = "multiSelect",
  Note = "note",
  // NLP = 'nlp',
  // Notification = 'notification',
  OpenOrClose = "openOrClose",
  // PastMessages = 'pastMessages',
  ProfileCollect = "profileCollect",
  Quest = "quest",
  Random = "random",
  // Route = 'route',
  RouterStart = "routerStart",
  Save = "save",
  // Sentiment = 'sentiment',
  Slack = "slack",
  SMS = "SMS",
  SMSGroup = "SMSGroup",
  SpacePicker = "spacePicker",
  Start = "start",
  StoreAccessCodes = "storeAccessCodes",
  StoreFinderLookup = "storeFinderLookup",
  UserValidation = "userValidation",
  StoreGetTenant = "storeGetTenant",
  StoreLead = "storeLead",
  ShortURL = "shortLink",
  TableSaveRow = "tableSaveRow",
  TypingIndicator = "typingIndicator",
  URL = "url",
  ValidateNormalize = "validateNormalize",
  Voice = "voice",
  PaymentLink = "paymentLink",
  Ticket = "ticket"
}

export interface NodeInterface {
  id: string;
  type: NodeType;
  [key: string]: any;
}

export interface NodeData_TableSaveRowNode extends NodeInterface {
  id: string;
  type: NodeType.TableSaveRow;
  table?: string;
  onSaveGoto?: string;
  onMissingParamsGoto?: string;
  update?: boolean;
  data: { [Property in keyof Partial<GenericTableColumns>]: string };
}

export interface NodeData_Array extends NodeInterface {
  id: string;
  type: NodeType.Array;
  action: "ADD" | "REMOVE" | "CLEAR" | "HAS" | "COUNT";
  variableId?: string;
  goto?: string;
  hasNotGoto?: string;
  greaterThanGoto?: string;
  lessThanGoto?: string;
  equalToGoto?: string;
  values?: string;
  count?: number;
}

export interface NodeData_BrainResult extends NodeInterface {
  id: string;
  type: NodeType.BrainResult;
  query: string; // Variable with user's message
  intent: string; // Variable from LLM structured output
  confidence?: string; // Optional variable for confidence score
  promptSlug: string; // Selected prompt slug
  brainLabel: string; // Brain label for Interactions page display
  onCompleteGoto?: string; // Single routing path
}

export interface NodeData_Note extends NodeInterface {
  id: string;
  type: NodeType.Note;
  updateExistingNote?: boolean;
  optionalSlug?: string;
  saveToFMS?: boolean;
  title: string;
  content: string;
  goto: string;
}
export interface NodeData_Increment extends NodeInterface {
  id: string;
  type: NodeType.Increment;
  variableId?: string;
  goto?: string;
  amount: number;
}

export interface NodeData_ArrayPicker extends NodeInterface {
  id: string;
  type: NodeType.ArrayPicker;
  variableId?: string;
  setToVariableId?: string;

  shouldPickOnlyItemAutomatically?: boolean;
  onPickedGoto?: string;
  onlyItemAutomaticallyPickedGoto?: string;
  noItemsGoto?: string;
  unhandledOutput?: {
    id: string;
    goto?: string;
  };
}
export interface NodeData_LeasePicker extends NodeInterface {
  id: string;
  type: NodeType.LeasePicker;
  initialMultipleLeasesMessage?: string;
  shouldPickOnlyItemAutomatically?: boolean;
  includeInactive?: boolean;
  onPickedGoto?: string;
  onlyItemAutomaticallyPickedGoto?: string;
  noLeasesGoto?: string;
  // Voice mode fields
  mode?: "web" | "voice";
  voiceOptionsVariable?: string;
  voiceOptionsSavedGoto?: string;
  voiceOnlyLeaseVariable?: string;
}
export enum LoadDataLoaders {
  NearbyLocationsAndUnits = "NearbyLocationsAndUnits"
}
export interface NodeData_LoadData extends NodeInterface {
  id: string;
  type: NodeType.LoadData;
  loader: LoadDataLoaders;
  params?: { [key: string]: string };
  goto?: string;
  onErrorGoto?: string;
}
export interface NodeData_StoreFinderLookup extends NodeInterface {
  id: string;
  type: NodeType.StoreFinderLookup;
  storeFoundGoto: string;
  storeNotSelectedGoto: string;
  missingParamsGoto: string;
  useURL: boolean;
  usePostalCode: boolean;
  useFMSId: boolean;
  filterStores?: boolean;
  filterVariableId?: string;
}

export interface NodeData_MergeUserNode extends NodeInterface {}

export interface NodeData_UserValidationNode extends NodeInterface {
  id: string;
  type: NodeType.UserValidation;
  phoneNumberVar?: VarKey;
  action: "SEND_CODE" | "VALIDATE";
  codeName?: string;
  validateForTimeInMinutes?: number; // in minutes
  revalidate?: boolean;
  goto?: string;
  invalidGoto?: string;
  smsMessage?: string;
  inputMessage?: string;
}
export interface NodeData_ShortURL extends NodeInterface {
  type: NodeType.ShortURL;
  saveToVar: VarKey;
  url: string;
  expiresAfterInMinutes?: number; // in minutes
  goto?: string;
}

export interface NodeData_PaymentLink extends NodeInterface {
  type: NodeType.PaymentLink;
  saveToVar: VarKey;
  expiresAfterInMinutes?: number;
  mode?: PaymentSessionMode;
  goto?: string;
  channel?: string;
  source?: string;
}

export enum LLMNodeMode {
  TEXT = "text",
  STRUCTURED = "structured",
  SEARCH = "search"
}

export interface NodeData_LLM extends NodeInterface {
  id: string;
  type: NodeType.LLM;
  mode: LLMNodeMode;

  // Text mode (existing functionality)
  prompts: PromptStackItem[];
  numberOfTokensToReturn: number;

  // Structured/Search mode
  selectedPromptSlug?: string;

  // Routing options
  goto?: string;
  successGoto?: string;
  errorGoto?: string;

  // Legacy compatibility
  openLinksInNewTab?: boolean;
}

export enum VoiceNodeCommandType {
  HANGUP = "hangup",
  FORWARD = "forward",
  CALL = "call",
  MEDIA = "media"
}
export interface VoiceNodeCommand_Hangup {
  type: VoiceNodeCommandType.HANGUP;
  message?: string;
}
export interface VoiceNodeCommand_Forward {
  type: VoiceNodeCommandType.FORWARD;
  to: string;
  message?: string;
  sendDigits?: string;
}
export interface VoiceNodeCommand_Call {
  type: VoiceNodeCommandType.CALL;
  to: string;
  from?: string;
  initialMessage?: string;
  sendDigits?: string;
}
export interface VoiceNodeCommand_Media {
  type: VoiceNodeCommandType.MEDIA;
  uri: string;
}

export type VoiceNodeCommand = VoiceNodeCommand_Hangup | VoiceNodeCommand_Forward | VoiceNodeCommand_Call | VoiceNodeCommand_Media;

export interface NodeData_Voice extends NodeInterface {
  id: string;
  type: NodeType.Voice;
  command: VoiceNodeCommand;
  goto?: string;
  machineAnsweredGoto?: string;
  noAnswerGoto?: string;
  timeoutGoto?: string;
  amdHumanQuest?: string;
  amdMachineQuest?: string;
  amdFallbackQuest?: string;
  blockHumanInterrupts?: boolean;
  interruptible?: boolean;
}

// Ticket Node Types
export enum TicketNodeMode {
  CREATE = "create",
  CREATE_OR_UPDATE = "create_or_update",
  UPDATE_ONLY = "update_only"
}

/** @deprecated Use TicketNodeMode instead - kept for backwards compatibility */
export enum TicketNodeNotFoundBehavior {
  CREATE_NEW = "create_new",
  UPSERT = "upsert",
  DO_NOTHING = "do_nothing"
}

export interface NodeData_Ticket extends NodeInterface {
  id: string;
  type: NodeType.Ticket;
  mode: TicketNodeMode;
  nodeSlug: string;
  /** @deprecated Use mode instead */
  notFoundBehavior?: TicketNodeNotFoundBehavior;
  templateId?: number;
  title: string;
  assigneeMemberIds?: number[];
  note?: string;
  status?: TicketStatus;
  tagToAdd?: string;
  priority?: TicketPriority;
  // Output paths
  goto: string; // Default output for CREATE mode
  gotoCreated?: string; // Output when ticket created (CREATE_OR_UPDATE mode)
  gotoUpdated?: string; // Output when ticket updated (CREATE_OR_UPDATE, UPDATE_ONLY modes)
  gotoNotFound?: string; // Output when no ticket found (UPDATE_ONLY mode)
}
